/**
 * venueRegistry.js — the tradable coin universe, and which exchange each ticker
 * lives on.
 *
 * Built from each venue's INSTRUMENTS endpoint, never from its 24h ticker feed.
 * That distinction is the whole point of this file:
 *
 *   • Binance's ticker feed lists ~680 USDT symbols, ~150 of which are SETTLING
 *     (delisted, winding down). They have no price and no chart, so they showed
 *     up at the bottom of the coin list as dead rows — BOB, BAL, DEFI, MBOX,
 *     STPT and friends. exchangeInfo with PERPETUAL + TRADING gives 530.
 *
 *   • Bybit, OKX and Bitget now list TOKENISED EQUITIES AND COMMODITIES as USDT
 *     perps: MA is Mastercard, ABNB is Airbnb, XAU is gold, CL is crude oil.
 *     Structurally they are ordinary linear perps, so the only honest filter is
 *     the flag each venue actually publishes (verified 05.08.2026):
 *       Bybit  → symbolType === 'stock'
 *       Bitget → isRwa === 'YES'
 *       OKX    → instCategory: '1' crypto · '3' equities · '4' commodities
 *     A hand-written ticker deny-list was the alternative; it would rot the
 *     first time a venue listed one more.
 *
 * Until the registry loads every coin reads as Binance and the universe is
 * empty — the answer the app gave before other exchanges existed, so a slow
 * load degrades to the old behaviour rather than to a wrong one.
 */
import { isStablePair, isExchangeLive } from './venues.js';

// symbol → venue, for coins Binance does not list.
let map = new Map();
// Every tradable ticker we accept, on any venue.
let universe = new Set();
let inflight = null;
let loadedAt = 0;

/**
 * The exchange a ticker's price and chart come from.
 *
 * The asset registry is asked first and wins. Both modules read the same
 * instrument lists, but they choose differently when an asset is on several
 * non-Binance venues: this one keeps whichever loader answered first, the asset
 * registry picks the busiest and makes it the BASE listing that charts, levels
 * and situations are drawn in. Six assets disagreed (OKB, CRO, CORE, BLEND,
 * DATA, 10000NEX) and their charts would have been fetched from the wrong
 * exchange — quietly, since the wrong venue still returns candles.
 */
export function venueOfSymbol(symbol) {
  const fromAssets = assetVenueOf(symbol);
  if (fromAssets) return fromAssets;
  return map.get(symbol) || 'binance';
}

// Injected at boot to keep this module free of an import cycle.
let assetVenueOf = () => null;
export function setAssetVenueResolver(fn) {
  if (typeof fn === 'function') assetVenueOf = fn;
}

/** Is this ticker part of the tradable universe at all? */
export function isKnownSymbol(symbol) {
  return universe.has(symbol);
}

export function venueOnlyCount() {
  return map.size;
}

export function universeSize() {
  return universe.size;
}

export function registryLoaded() {
  return loadedAt > 0;
}

const canonical = (s) => String(s || '').toUpperCase().replace(/-/g, '').replace(/SWAP$/, '');

const keep = (sym) => sym.endsWith('USDT') && !isStablePair(sym);

/** Binance perpetuals that are actually trading (not SETTLING / delisted). */
async function binanceSymbols() {
  const r = await fetch('https://fapi.binance.com/fapi/v1/exchangeInfo');
  if (!r.ok) throw new Error(`binance ${r.status}`);
  const j = await r.json();
  return (j.symbols || [])
    .filter((s) => s.contractType === 'PERPETUAL' && s.status === 'TRADING')
    .map((s) => s.symbol)
    .filter(keep);
}

/**
 * Bybit's symbolType, measured 05.08.2026 over its live linear perps:
 *   ''           397  mainstream crypto
 *   'innovation' 116  crypto too — new/high-risk listings, NOT to be dropped
 *   'stock'      165  tokenised equities (AAPL, AMZN, Mastercard…)
 *   'commodity'    4  BZ, CL, XAG, XAU
 *
 * So it is a deny-list, not "keep the empty one": allowing only '' would have
 * silently thrown away 116 real coins. `venueRegistry.test.mjs` fails if Bybit
 * introduces a type that is not classified here, so a new category becomes a
 * decision rather than a silent inclusion.
 *
 * Found live 20.08.2026, exactly the failure mode this guard exists for:
 * Bybit added a FIFTH type, `'ETF'` (SOXLUSDT — a 3x leveraged semiconductor
 * ETF, symbolType:"ETF", baseCoin:"SOXL") — same non-crypto-tradeable-as-a-
 * coin problem as 'stock'/'commodity', same reasoning as the stablecoin fix
 * in lib/stablecoins.js: a detector scanning it for "support bounce" would
 * be reasoning about a 3x-leveraged decay product as if it were a coin. The
 * guard test (`BYBIT_KNOWN_SYMBOL_TYPES`, below) DID exist and would have
 * caught this on its own — but it runs after the plain equities-exclusion
 * assertion in venueRegistry.test.mjs, which failed first on the SOXLUSDT
 * leak with a generic message, so the specific "classify this new type"
 * guidance from the type-guard assertion never got a chance to fire. Not
 * reordering the test (out of scope here) — noting it so a future instance
 * of this doesn't read as "the guard is broken", it just never got to run.
 */
const BYBIT_NON_CRYPTO = new Set(['stock', 'commodity', 'ETF', 'forex']);
// 13.09.2026: a SIXTH type, `'forex'` — EURUSDUSDT, GBPUSDUSDT, USDJPYUSDT.
// Currency pairs quoted as USDT perps; not coins, same call as 'stock' above.
// The server's copy (venueBackfill.js) is kept in step — see its own note.
export const BYBIT_KNOWN_SYMBOL_TYPES = new Set(['', 'innovation', 'stock', 'commodity', 'ETF', 'forex']);

async function bybitSymbols() {
  const out = [];
  let cursor = '';
  // The instruments endpoint pages at 1000 — one more way it differs from the
  // ticker feed this used to be built from.
  for (let page = 0; page < 5; page++) {
    const u = `https://api.bybit.com/v5/market/instruments-info?category=linear&limit=1000${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
    const r = await fetch(u);
    if (!r.ok) throw new Error(`bybit ${r.status}`);
    const j = await r.json();
    for (const it of j?.result?.list || []) {
      if (it.contractType !== 'LinearPerpetual' || it.status !== 'Trading') continue;
      if (BYBIT_NON_CRYPTO.has(it.symbolType)) continue;
      if (keep(it.symbol)) out.push(it.symbol);
    }
    cursor = j?.result?.nextPageCursor || '';
    if (!cursor) break;
  }
  return out;
}

async function okxSymbols() {
  const r = await fetch('https://www.okx.com/api/v5/public/instruments?instType=SWAP');
  if (!r.ok) throw new Error(`okx ${r.status}`);
  const j = await r.json();
  return (j.data || [])
    .filter((x) => x.settleCcy === 'USDT' && x.state === 'live' && x.instCategory === '1')
    .map((x) => canonical(x.instId))
    .filter(keep);
}

async function bitgetSymbols() {
  const r = await fetch('https://api.bitget.com/api/v2/mix/market/contracts?productType=usdt-futures');
  if (!r.ok) throw new Error(`bitget ${r.status}`);
  const j = await r.json();
  return (j.data || [])
    .filter((x) => x.symbolStatus === 'normal' && x.isRwa !== 'YES')
    .map((x) => x.symbol)
    .filter(keep);
}

// Only exchanges that are live on the site (venues.js LIVE_EXCHANGE_IDS) are
// asked; an "In development" one adds no coins and costs no request.
const VENUE_LOADERS = [
  ['bybit', bybitSymbols],
  ['okx', okxSymbols],
  ['bitget', bitgetSymbols],
].filter(([venue]) => isExchangeLive(venue));

/**
 * Load once per session; concurrent callers share the request.
 *
 * A failure leaves the previous state in place. An empty registry would claim
 * every coin is on Binance and let orders through for coins that are not listed
 * there, so "we could not refresh" must never read as "everything moved to
 * Binance".
 */
export function loadVenueRegistry() {
  if (inflight) return inflight;
  inflight = (async () => {
    const binance = await binanceSymbols();
    if (!binance.length) return map;         // Binance list failed → trust nothing
    const binanceSet = new Set(binance);

    const nextUniverse = new Set(binance);
    const nextMap = new Map();

    // One venue being unreachable must not drop the rest of the universe.
    const results = await Promise.allSettled(VENUE_LOADERS.map(([, fn]) => fn()));
    results.forEach((res, i) => {
      const venue = VENUE_LOADERS[i][0];
      if (res.status !== 'fulfilled') {
        console.warn(`[venueRegistry] ${venue} unavailable:`, res.reason?.message);
        return;
      }
      for (const sym of res.value) {
        nextUniverse.add(sym);
        // Binance wins: a coin listed there is never re-pointed elsewhere, and
        // the first other venue to claim a Binance-absent coin keeps it.
        if (!binanceSet.has(sym) && !nextMap.has(sym)) nextMap.set(sym, venue);
      }
    });

    map = nextMap;
    universe = nextUniverse;
    loadedAt = Date.now();
    return map;
  })()
    .catch((e) => {
      console.warn('[venueRegistry] load failed:', e.message);
      return map;
    })
    .finally(() => { inflight = null; });
  return inflight;
}
